import { useEffect, useState } from 'react';
import { Camera, FlipHorizontal, RotateCcw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useCamera } from '../../hooks/useCamera';

interface Props {
  /** Called with the JPEG blob when the user confirms a shot */
  onCapture: (blob: Blob) => void;
  /** Optional: auto-start the camera when component mounts */
  autoStart?: boolean;
}

export function CameraCapture({ onCapture, autoStart = true }: Props) {
  const { videoRef, isStreaming, isStarting, error, facing, startCamera, stopCamera, switchCamera, takePicture } =
    useCamera();

  const [preview, setPreview] = useState<string | null>(null);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (autoStart) {
      startCamera('user');
    }
    return () => {
      stopCamera();
    };
  }, [autoStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTakePicture = () => {
    const blob = takePicture();
    if (!blob) return;
    setPendingBlob(blob);
    const url = URL.createObjectURL(blob);
    setPreview(url);
  };

  const handleRetake = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setPendingBlob(null);
  };

  const handleConfirm = () => {
    if (!pendingBlob) return;
    onCapture(pendingBlob);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setPendingBlob(null);
  };

  // Revoke object URL on unmount
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Viewfinder / preview area */}
      <div className="relative w-full max-w-md rounded-xl overflow-hidden bg-gray-900 aspect-video">
        {/* Live video — hidden when preview is shown */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${preview ? 'hidden' : 'block'} ${
            facing === 'user' ? 'scale-x-[-1]' : ''
          }`}
        />

        {/* Preview image after taking a shot */}
        {preview && (
          <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
        )}

        {/* Loading overlay */}
        {isStarting && !preview && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white bg-gray-900">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Activation de la caméra…</span>
          </div>
        )}

        {/* Error overlay */}
        {error && !preview && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white bg-gray-900 p-4">
            <AlertCircle className="w-10 h-10 text-red-400" />
            <p className="text-sm text-center text-red-300">{error}</p>
            <button
              type="button"
              onClick={() => startCamera(facing)}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Idle state: no stream, no error */}
        {!isStreaming && !isStarting && !error && !preview && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white bg-gray-900">
            <Camera className="w-10 h-10 text-gray-400" />
            <p className="text-sm text-gray-400">Caméra non démarrée</p>
            <button
              type="button"
              onClick={() => startCamera('user')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Démarrer la caméra
            </button>
          </div>
        )}

        {/* Camera switch button (top-right) — only visible when streaming and no preview */}
        {isStreaming && !preview && (
          <button
            type="button"
            onClick={switchCamera}
            title="Changer de caméra (avant/arrière)"
            className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <FlipHorizontal className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!preview ? (
          <button
            type="button"
            onClick={handleTakePicture}
            disabled={!isStreaming}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Camera className="w-5 h-5" />
            Prendre la photo
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleRetake}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reprendre
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Confirmer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
